import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../router';
import { BCPList } from '../components/BCPList';
import BCPStepIndicator from '../components/BCPStepIndicator';
import BCPPageHeader from '../components/BCPPageHeader';
import type { BCPListItem } from '../types/bcp';
import { useHeaderMenuItems } from '../hooks/useHeaderMenuItems';
import aiSupportIcon from '../assets/images/icon/ai_support.png';

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  const handleBCPCreate = (bcpType: 'introductory' | 'simple' | 'basic') => {
    navigate(ROUTES.UPLOAD, { state: { bcpType } });
  };

  const handleBCPSelect = (bcp: BCPListItem) => {
    // Navigate to appropriate BCP creation page in edit mode
    let route: string;
    if (bcp.type === 'introductory') {
      route = '/create-bcp-intro';
    } else if (bcp.type === 'basic') {
      // Basic BCP: navigate to first section (restore-policy)
      route = `/bcp/${bcp.id}/restore-policy`;
      navigate(route + `?tenantId=${bcp.tenant_id}`);
      return;
    } else {
      // Simple BCP
      route = '/create-bcp';
    }

    const finalUrl = `${route}?bcpId=${bcp.id}&tenantId=${bcp.tenant_id}`;

    // Add query parameters for edit mode
    navigate(finalUrl);
  };

  const homeMenuItems = useHeaderMenuItems();

  return (
    <>
      <BCPPageHeader
        showBackButton={false}
        menuItems={homeMenuItems}
      />
      <div className="bg-gray-50">
        <div className="max-w-7xl mx-auto px-8 py-8">
          {/* BCP作成の流れセクション */}
          <div className="mb-10">
            {/* 見出し */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#0d357f] mb-2">
                BCP作成の流れ
              </h2>
              <p className="text-base text-[#4a5565] font-light">
                簡単3ステップで、あなたの会社のBCPを作成
              </p>
            </div>

            {/* 3ステップフロー */}
            <div className="flex flex-col md:flex-row items-start justify-center gap-6 mb-8">
              {/* ステップ1: 会社情報インプット */}
              <div className="relative w-full md:w-[300px] h-[238px] flex-shrink-0" style={{ borderRadius: '16px', border: '1px solid #BEDBFF', background: 'linear-gradient(to right, rgba(190, 219, 255, 0.02), rgba(190, 219, 255, 0.08))', boxShadow: '0 4px 12px 0 rgba(13, 53, 127, 0.10)' }}>
                {/* アイコン */}
                <div className="absolute left-1/2 top-[32px] -translate-x-1/2 w-16 h-16 bg-[#0d357f] rounded-full shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-4px_rgba(0,0,0,0.1)] flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                {/* コンテンツ */}
                <div className="absolute left-[32px] top-[116px] w-[236px] h-[90px]">
                  <h3 className="text-lg font-bold text-[#101828] text-center leading-[26px]">
                    会社情報インプット
                  </h3>
                  <div className="flex flex-col gap-2 mt-[12px]">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-sm text-[#4a5565] font-[350] leading-[22px]">
                        会社紹介資料をアップロード
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                      <p className="text-sm text-[#4a5565] font-[350] leading-[22px]">
                        最低限の会社情報を入力
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 矢印 (デスクトップのみ表示) */}
              <div className="hidden md:flex items-center justify-center self-center">
                <svg className="w-10 h-10 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>

              {/* ステップ2: AIサポートで作成 */}
              <div className="relative w-full md:w-[300px] h-[238px] flex-shrink-0" style={{ borderRadius: '16px', border: '1px solid #E9D4FF', background: 'linear-gradient(to right, rgba(233, 212, 255, 0.02), rgba(233, 212, 255, 0.08))', boxShadow: '0 4px 12px 0 rgba(13, 53, 127, 0.10)' }}>
                {/* アイコン */}
                <div className="absolute left-[118px] top-[35.5px] w-16 h-16 bg-[#0d357f] rounded-full shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-4px_rgba(0,0,0,0.1)] flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                {/* コンテンツ */}
                <div className="absolute left-[32px] top-[116px] w-[236px] h-[90px]">
                  <h3 className="text-lg font-bold text-[#101828] text-center leading-[26px]">
                    AIサポートで作成
                  </h3>
                  <div className="flex flex-col gap-2 mt-[12px]">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-sm text-[#4a5565] font-[350] leading-[22px]">
                        入力情報からAIが下書きを生成
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <img
                        src={aiSupportIcon}
                        alt="AIサポート"
                        className="w-4 h-4 flex-shrink-0"
                      />
                      <p className="text-sm text-[#4a5565] font-[350] leading-[22px]">
                        記載に迷ったらAIに相談できる
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 矢印 (デスクトップのみ表示) */}
              <div className="hidden md:flex items-center justify-center self-center">
                <svg className="w-10 h-10 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>

              {/* ステップ3: ダウンロード */}
              <div className="relative w-full md:w-[300px] h-[238px] flex-shrink-0" style={{ borderRadius: '16px', border: '1px solid #B9F8CF', background: 'linear-gradient(to right, rgba(185, 248, 207, 0.02), rgba(185, 248, 207, 0.08))', boxShadow: '0 4px 12px 0 rgba(13, 53, 127, 0.10)' }}>
                {/* アイコン */}
                <div className="absolute left-[118px] top-[32px] w-16 h-16 bg-gradient-to-b from-[#0d357f] to-[#1e4a9a] rounded-full shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-4px_rgba(0,0,0,0.1)] flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </div>
                {/* コンテンツ */}
                <div className="absolute left-[32px] top-[116px] w-[236px] h-[90px]">
                  <h3 className="text-lg font-bold text-[#101828] text-center leading-[26px]">
                    ダウンロード
                  </h3>
                  <div className="flex flex-col gap-2 mt-[12px]">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      <p className="text-sm text-[#4a5565] font-[350] leading-[22px]">
                        完成したBCPをダウンロード
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <p className="text-sm text-[#4a5565] font-[350] leading-[22px]">
                        すぐに活用開始
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 新規BCP作成セクション */}
          <div className="mb-10">
            <h2 className="text-xl font-bold text-[#0d357f] mb-6">
              新規BCP作成
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              {/* 超入門版BCP */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-md p-6 flex flex-col">
                <div className="flex items-start gap-2 mb-3">
                  <svg className="w-5 h-5 text-[#00a63e] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-lg font-bold text-gray-900">
                    超入門版BCP(首都直下地震対策)
                  </h3>
                </div>
                <p className="text-sm text-[#4a5565] font-light mb-3 leading-5 min-h-[80px]">
                  首都直下地震発生時に「誰が何をするか」「重要事業と必要資源」「事前準備」の３点について、最低限検討すべき項目をまとめたBCPを作成できます。
                </p>
                <a href="/超入門版_完成イメージ_v2.pdf" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0d357f] font-light mb-4 inline-flex items-center gap-1">
                  完成イメージを見る
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </a>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-[#4a5565] font-light">所要時間: </span>
                  <span className="text-sm text-[#4a5565] font-light">約15分</span>
                </div>
                <button
                  onClick={() => handleBCPCreate('introductory')}
                  className="w-full py-2 bg-[#00a63e] text-white text-sm font-medium rounded hover:bg-[#008c35] transition-colors mt-auto"
                >
                  作成する
                </button>
              </div>

              {/* 簡易版BCP */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-md p-6 flex flex-col">
                <div className="flex items-start gap-2 mb-3">
                  <svg className="w-5 h-5 text-[#0d357f] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-lg font-bold text-gray-900">
                    簡易版BCP(オールハザード型)
                  </h3>
                </div>
                <p className="text-sm text-[#4a5565] font-light mb-3 leading-5 min-h-[80px]">
                  あらゆるリスクに耐えられる想定をしたオールハザード型BCPを簡易的に作成できます。重要事業に必要な経営資源等について検討します。
                </p>
                <a href="/簡易版_完成イメージ_v2.pdf" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0d357f] font-light mb-4 inline-flex items-center gap-1">
                  完成イメージを見る
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </a>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-[#4a5565] font-light">所要時間: </span>
                  <span className="text-sm text-[#4a5565] font-light">約30分</span>
                </div>
                <button
                  onClick={() => handleBCPCreate('simple')}
                  className="w-full py-2 bg-[#0d357f] text-white text-sm font-medium rounded hover:bg-[#0a2a5f] transition-colors mt-auto"
                >
                  作成する
                </button>
              </div>

              {/* 基本版BCP */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-md p-6 flex flex-col">
                <div className="flex items-start gap-2 mb-3">
                  <svg className="w-5 h-5 text-[#f54900] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-lg font-bold text-gray-900">
                    基本版BCP(オールハザード型)
                  </h3>
                </div>
                <p className="text-sm text-[#4a5565] font-light mb-3 leading-5 min-h-[80px]">
                  「復旧方針書」「BCP規程」「初動対応表」「手順書」を作成し、基本的な要点をまとめたBCPを作成します。
                </p>
                <a href="/基本版概要版_完成イメージ_v2.pdf" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0d357f] font-light mb-4 inline-flex items-center gap-1">
                  完成イメージを見る
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </a>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-[#4a5565] font-light">所要時間: </span>
                  <span className="text-sm text-[#4a5565] font-light">約1〜2時間</span>
                </div>
                <button
                  onClick={() => handleBCPCreate('basic')}
                  className="w-full py-2 bg-[#f54900] text-white text-sm font-medium rounded hover:bg-[#d43f00] transition-colors mt-auto"
                >
                  作成する
                </button>
              </div>
            </div>
          </div>

          {/* BCP一覧 */}
          <BCPList
            tenantId="default"
            onSelect={handleBCPSelect}
            pageSize={10}
          />
        </div>
      </div>
    </>
  );
};

export default HomePage;
